import { useAppInfiniteQuery } from "@/hooks/useAppInfiniteQuery"
import { useAppQuery } from "@/hooks/useAppQuery"
import {
  type GetUserSharedDatasParams,
  getGetUserSharedDatasQueryKey,
  getUserSharedDatas,
} from "@/networking/api"
import { STALE_TIME_FIVE_MINUTES_IN_MS } from "@/utils/constants"
import { useMemo } from "react"

type UseAppInfiniteQueryOptions = Parameters<
  typeof useAppInfiniteQuery<
    GetUserSharedDatasParams,
    Awaited<ReturnType<typeof getUserSharedDatas>>["hydra:member"][number],
    unknown,
    ReturnType<typeof getGetUserSharedDatasQueryKey>
  >
>[3]

type UseAppQueryOptions = Parameters<
  typeof useAppQuery<
    GetUserSharedDatasParams,
    Awaited<ReturnType<typeof getUserSharedDatas>>["hydra:member"][number],
    unknown,
    ReturnType<typeof getGetUserSharedDatasQueryKey>
  >
>[3]

export const useGetInfiniteQuery = (
  params: GetUserSharedDatasParams,
  options?: UseAppInfiniteQueryOptions
) => {
  const query = useAppInfiniteQuery(getUserSharedDatas, getGetUserSharedDatasQueryKey, params, {
    staleTime: STALE_TIME_FIVE_MINUTES_IN_MS,
    ...options,
  })
  const isLoading = useMemo(
    () => query.isLoading || query.isFetching || query.isRefetching || query.isFetchingNextPage,
    [query.isLoading, query.isFetching, query.isRefetching, query.isFetchingNextPage]
  )
  // const fetchNextPage = useMemo(() => query.fetchNextPage, [query])
  // const hasNextPage = useMemo(() => query.hasNextPage, [query])
  const items = useMemo(() => query.flatData, [query.flatData])
  const rawItems = useMemo(() => query.rawData, [query.rawData])
  const count = useMemo(() => query.totalCount, [query.totalCount])

  return { ...query, query, items, rawItems, isLoading, count }
}

export const useGetFirstPageQuery = (
  params: GetUserSharedDatasParams,
  options?: UseAppQueryOptions
) => {
  const modifiedParams = {
    page: 1,
    itemsPerPage: 20,
    ...params,
  }

  const query = useAppQuery(getUserSharedDatas, getGetUserSharedDatasQueryKey, modifiedParams, {
    staleTime: STALE_TIME_FIVE_MINUTES_IN_MS,
    ...options,
  })

  const isLoading = useMemo(
    () => query.isLoading || query.isFetching || query.isRefetching,
    [query.isLoading, query.isFetching, query.isRefetching]
  )
  const items = useMemo(() => query.flatData, [query.flatData])
  const count = useMemo(() => query.totalCount, [query.totalCount])

  return { query, items, isLoading, count }
}
